import { Button, Checkbox, Popover, Toggle, usePopper } from 'dodoc-design-system';
import { FC, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styles from './TableColumnsToggle.module.scss';
import { useEditUserSettingsMutation, useGetUserSettingsQuery } from '_common/services/api/UserApi';
import { COLUMNS_TRANSLATION_IDS } from '_common/consts';
import useVeevaIntegration from '_common/hooks/useVeevaIntegration';

const TABLE_COLUMNS = [
  'status',
  'time.modification',
  'shared_with',
  'owner',
  'time.creation',
] as const;

const SEARCH_TABLE_COLUMNS = [
  'status',
  'time.modification',
  'location',
  'shared_with',
  'owner',
  'time.creation',
] as const;

type TableColumn =
  | (typeof TABLE_COLUMNS)[number]
  | (typeof SEARCH_TABLE_COLUMNS)[number]
  | 'metadata.veeva_doc_number'
  | 'metadata.veeva_global_id';

function useTableColumns(table: keyof ApiSchemas['UserSettingsSchema']['tables']) {
  const intl = useIntl();
  const { data } = useGetUserSettingsQuery();
  const hasVeevaIntegration = useVeevaIntegration();

  const tableColumns = useMemo<TableColumn[]>(() => {
    if (hasVeevaIntegration && table === 'explorer') {
      return [...TABLE_COLUMNS, 'metadata.veeva_doc_number', 'metadata.veeva_global_id'];
    } else if (table === 'search') {
      return [...SEARCH_TABLE_COLUMNS];
    }
    return [...TABLE_COLUMNS];
  }, [hasVeevaIntegration, table]);

  return useMemo(() => {
    if (!data) return [];

    return tableColumns.map((column) => ({
      id: column,
      label: COLUMNS_TRANSLATION_IDS[column]
        ? intl.formatMessage({ id: COLUMNS_TRANSLATION_IDS[column] })
        : '',
      checked: data.tables[table][column],
    }));
  }, [data, tableColumns]);
}

export type Column = {
  id: string;
  label: string;
  checked: boolean;
};
type TableColumnsToggleProps = { table: keyof ApiSchemas['UserSettingsSchema']['tables'] };

const TableColumnsToggle: FC<TableColumnsToggleProps> = ({ table }) => {
  const [editUserSettings] = useEditUserSettingsMutation();
  const columns = useTableColumns(table);
  const { popperProps, referenceProps, isOpen, close } = usePopper({ placement: 'bottom-end' });

  const [options, setOptions] = useState([...columns]);

  useEffect(() => {
    setOptions([...columns]);
  }, [isOpen]);

  const onChange = (columnId: string) => {
    const index = options.findIndex((column) => column.id === columnId);
    const newColumnsState = [...options];
    newColumnsState.splice(index, 1, { ...options[index], checked: !options[index].checked });
    setOptions(newColumnsState);
  };

  const onClick = () => {
    const newSettings = options.reduce<
      ApiOperations['edit_user_settings']['requestBody']['content']['multipart/form-data']
    >((previous, current) => {
      //@ts-expect-error The metadata.veeva_doc_number and metadata.veeva_global_id may not exist
      previous[`tables.${table}.${current.id}`] = current.checked;
      return previous;
    }, {});
    editUserSettings(newSettings);
    close();
  };

  const isButtonDisabled = useMemo(() => {
    return options.every((option, index) => option.checked === columns[index].checked);
  }, [options, columns]);

  return (
    <>
      <Toggle
        variant="ghost"
        size="medium"
        testId="display-columns-toggle"
        dropdown
        margin="0 0 0 2rem"
        isToggled={isOpen}
        {...referenceProps}
      >
        <FormattedMessage id="DISPLAY_COLUMNS" />
      </Toggle>
      <Popover testId="display-columns-popover" {...popperProps}>
        <div className={styles.popover}>
          <div className={styles.title}>
            <FormattedMessage id="DISPLAY_COLUMNS" />
          </div>
          <div className={styles.checkboxes}>
            {options.map(({ id, label, checked }, index) => (
              <Checkbox
                key={id}
                checked={checked ? 'checked' : 'unchecked'}
                testId={`${id}-checkbox`}
                margin={index === 0 ? '' : '2rem 0 0 0'}
                size="x-small"
                onChange={() => onChange(id)}
              >
                {label}
              </Checkbox>
            ))}
          </div>
          <div className={styles.button}>
            <Button
              size="small"
              testId="display-columns-apply-button"
              variant="primary"
              margin="3rem 0 0 0"
              onClick={onClick}
              disabled={isButtonDisabled}
            >
              <FormattedMessage id="global.apply" />
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default TableColumnsToggle;
